// "A new version is available → Refresh" toast.
//
// **Why this exists:** the console is a plain Vite SPA — no service
// worker, no PWA (see `main.tsx`: "No service worker. No PWA."). When a
// new bundle deploys, a user on an open tab sits on the stale build
// until they manually hard-refresh. This toast gives them a signal +
// a one-click `window.location.reload()`.
//
// **Mechanism (deployed):** the build bakes a `__APP_BUILD_ID__` into
// the bundle (Vite `define`) AND emits the SAME id to `dist/version.json`
// (the `sb-version-json` plugin in `vite.config.ts`). At runtime the SPA
// polls `/version.json` (cache-busted, `cache: 'no-store'`) on an
// interval and on window focus; when the fetched `buildId` differs from
// the baked-in one, a NEW bundle has deployed → show the toast.
//
// **Inert in dev (HARD requirement):** `vite dev`/`vite preview` do not
// emit `version.json` (the plugin's `writeBundle` only runs on `build`),
// so the fetch 404s. `vite preview` serves a real build whose id matches
// the bundle's. In all dev cases `isNewVersionAvailable` returns `false`
// (missing/404/sentinel/equal id) and a fetch error is swallowed — the
// toast NEVER shows on a false positive. The check is a no-op until a
// genuinely different `version.json` appears.
//
// **Test / Storybook seam** (same shape as `UserMenu` /
// `PendingApprovalsBadge`): the `available` prop bypasses the live hook,
// so the unit test renders the View without any network. The hook is
// only invoked in the `Live` branch.

import { useEffect, useState } from 'react';

/**
 * Poll interval (ms) for the `/version.json` check. ~60s is frequent
 * enough that a user notices a deploy within a minute, infrequent
 * enough to be invisible cost. Exported so tests can reason about it.
 */
export const VERSION_CHECK_INTERVAL_MS = 60_000;

/** Where the build id is served from in deployed mode (dist root). */
export const VERSION_JSON_PATH = '/version.json';

/**
 * Build id baked into THIS bundle at build time. `__APP_BUILD_ID__` is
 * injected by the Vite `define` (see `vite.config.ts` + `vite-env.d.ts`).
 * Read defensively (`typeof`) so non-Vite contexts — Node, a test that
 * never set it — don't throw on the bare global reference.
 */
export function bakedBuildId(): string | undefined {
  return typeof __APP_BUILD_ID__ === 'string' ? __APP_BUILD_ID__ : undefined;
}

/**
 * Pure comparison: is the server-reported build id a DIFFERENT, real
 * deployed version than the one baked into this bundle?
 *
 * Returns `false` (stay inert — no toast) whenever:
 *   - either id is missing / not a non-empty string (dev sentinel,
 *     404 → `undefined`, malformed `version.json`);
 *   - the ids are equal (the running bundle IS the deployed one —
 *     `vite preview`, or the first load after a deploy).
 *
 * Returns `true` only when both ids are present, non-empty, and differ
 * — i.e. a newer bundle has shipped than the one this tab is running.
 */
export function isNewVersionAvailable(
  baked: string | null | undefined,
  fetched: string | null | undefined,
): boolean {
  if (typeof baked !== 'string' || baked.length === 0) return false;
  if (typeof fetched !== 'string' || fetched.length === 0) return false;
  return baked !== fetched;
}

/**
 * Fetch the deployed build id from `/version.json`. Cache-busted
 * (`?t=${Date.now()}` + `cache: 'no-store'`) so a CDN / browser cache
 * never serves a stale id and masks a fresh deploy. Returns `undefined`
 * on ANY failure (404 in dev, network error, non-OK status, malformed
 * JSON, missing `buildId`) so the caller treats it as "no new version".
 */
async function fetchDeployedBuildId(signal?: AbortSignal): Promise<string | undefined> {
  try {
    const response = await fetch(`${VERSION_JSON_PATH}?t=${Date.now()}`, {
      cache: 'no-store',
      ...(signal !== undefined ? { signal } : {}),
    });
    if (!response.ok) return undefined;
    const body: unknown = await response.json();
    const buildId = (body as { buildId?: unknown } | null)?.buildId;
    return typeof buildId === 'string' && buildId.length > 0 ? buildId : undefined;
  } catch {
    // Swallow EVERYTHING (404 in dev, offline, abort, parse error). A
    // failed check must NEVER surface a toast — silence is the inert
    // default the HARD requirement demands.
    return undefined;
  }
}

/**
 * Live hook: polls `/version.json` on an interval and on window focus,
 * compares against the baked-in id, and returns whether a new version
 * is available. Once `true`, it stays `true` (no point re-checking — the
 * user has already been told to refresh).
 *
 * In dev there is no `version.json` to fetch, so this stays `false`.
 */
export function useNewVersionAvailable(): boolean {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    // Already flagged → stop polling. The toast is shown; refreshing is
    // the only forward path.
    if (available) return;

    const baked = bakedBuildId();
    // No baked id means the bundle wasn't built with the `define` (e.g.
    // a stale build) — there is nothing to compare against, so never
    // toast. Cheap early-out that also keeps the interval from running.
    if (baked === undefined) return;

    let cancelled = false;
    const controller = new AbortController();

    async function check(): Promise<void> {
      const fetched = await fetchDeployedBuildId(controller.signal);
      if (cancelled) return;
      if (isNewVersionAvailable(baked, fetched)) setAvailable(true);
    }

    // Check once on mount, then on the interval + on window focus (a
    // user returning to a long-open tab sees a fresh result without
    // waiting out the interval — mirrors the pending-approvals badge's
    // focus-refetch intent).
    void check();
    const timer = setInterval(() => void check(), VERSION_CHECK_INTERVAL_MS);
    const onFocus = (): void => void check();
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [available]);

  return available;
}

export interface VersionToastProps {
  /**
   * Availability override — bypasses the live hook for tests / storybook.
   * When provided, the View renders directly (no interval, no fetch).
   */
  readonly available?: boolean;
}

/**
 * The version toast. Renders nothing until a new version is detected,
 * then a dismissible bottom-right toast with a Refresh button.
 *
 * **Test / Storybook seam**: the `available` prop bypasses the
 * `useNewVersionAvailable` hook entirely (the load-bearing reason the
 * rendering lives in a child `View`): the live hook is only invoked when
 * no override is supplied, so the unit test needs no network / timers.
 */
export function VersionToast(props: VersionToastProps): React.ReactElement | null {
  if (props.available !== undefined) {
    return <VersionToastView available={props.available} />;
  }
  return <VersionToastLive />;
}

function VersionToastLive(): React.ReactElement | null {
  const available = useNewVersionAvailable();
  return <VersionToastView available={available} />;
}

function VersionToastView({ available }: { available: boolean }): React.ReactElement | null {
  const [dismissed, setDismissed] = useState(false);

  if (!available || dismissed) return null;

  // `<output>` carries an implicit role="status" (so no explicit role needed);
  // the `.version-toast` class sets display:flex + position:fixed, so the
  // semantic-element swap from <div> leaves layout unchanged.
  return (
    <output className="version-toast" aria-live="polite" data-testid="version-toast">
      <span className="version-toast-message">A new version is available.</span>
      <button
        type="button"
        className="version-toast-refresh"
        onClick={() => window.location.reload()}
      >
        Refresh
      </button>
      <button
        type="button"
        className="version-toast-dismiss"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
      >
        ×
      </button>
    </output>
  );
}
